import { Outlet, createFileRoute, redirect } from '@tanstack/react-router';
import * as React from 'react';

import { AppContentArea } from '@/components/app/app-content-area';
import { AppSidebar } from '@/components/app/app-sidebar';
import { AppTopbar } from '@/components/app/app-topbar';
import { capabilitiesFor } from '@/components/app/capabilities';
import { MobileSidebarDrawer } from '@/components/app/mobile-sidebar-drawer';
import { OverdueCommissionBanner } from '@/components/billing';
import { safeInternalPath } from '@/lib/safe-redirect';
import { trpcVanilla } from '@/lib/trpc/shared';

export const Route = createFileRoute('/_app')({
  beforeLoad: async ({ location }) => {
    const me = await trpcVanilla.user.me.query().catch(() => null);
    if (!me) {
      const redirectTo = safeInternalPath(location.href);
      throw redirect({ to: '/sign-in', search: redirectTo ? { redirect: redirectTo } : {} });
    }
    if (me.status === 'PENDING') {
      throw redirect({ to: '/oauth-complete' });
    }
    if (me.status !== 'ACTIVE') {
      throw redirect({ to: '/sign-in' });
    }
    if (!me.emailVerified) {
      throw redirect({ to: '/check-email', search: { email: me.email } });
    }
    return { me };
  },
  component: AppLayout,
});

function AppLayout(): React.ReactElement {
  const { me } = Route.useRouteContext();
  const displayName = me.displayName ?? '';
  const email = me.email;
  const role = me.role;
  const bidderType = me.bidderType;
  const avatarUrl = me.avatarUrl ?? null;
  const capabilities = capabilitiesFor({
    isAdmin: me.isAdmin,
    hasRequesterProfile: me.hasRequesterProfile,
    hasBidderProfile: me.hasBidderProfile,
  });

  return (
    <>
      <a
        href="#main-content"
        className="focus:bg-primary focus:text-primary-foreground sr-only focus:not-sr-only focus:absolute focus:top-3 focus:left-3 focus:z-50 focus:rounded-md focus:px-3 focus:py-2"
      >
        Aller au contenu
      </a>
      <div className="bg-background flex h-dvh flex-col">
        <AppTopbar
          drawer={<MobileSidebarDrawer role={role} bidderType={bidderType} />}
          user={{ displayName, email, role, avatarUrl }}
          isAdmin={me.isAdmin}
          capabilities={capabilities}
        />
        <OverdueCommissionBanner />
        <div className="flex min-h-0 flex-1">
          <AppSidebar role={role} bidderType={bidderType} />
          <main
            id="main-content"
            className="bg-surface border-border flex min-w-0 flex-1 flex-col overflow-hidden rounded-tl-3xl border-t border-l"
          >
            <AppContentArea>
              <Outlet />
            </AppContentArea>
          </main>
        </div>
      </div>
    </>
  );
}
